/* eslint-disable max-len */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PlusIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/solid';
import { Switch } from '@headlessui/react';
import { ChartBarIcon, ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import ProjectTransactionList from './ProjectTransactionList';
import AccountLabel from '../../elements/AccountLabel';
import Notes from './Notes';
import ProgressRoi from './ProgressRoi';
import ProgressSlider2 from './ProgressSlider2';
import ProgressGoal from './ProgressGoal';
import AddProjectTransaction from './AddProjectTransaction';
import AddProjectQuote from './AddProjectQuote';
import ApplyProjectTemplate from './ApplyProjectTemplate';
import CollisionsDialog from './CollisionsDialog';
import FilterListbox from './FilterListbox';
import Slider from './Slider';
import { putProject, deleteProject } from '../../redux/actions/projects';
import { setMessage } from '../../redux/actions/message';
import {
  makeSelectProjectTransactionsById,
  makeSelectProjectAssetsSummary,
  postMixedData,
} from '../../redux/reducers/data';
import Button from '../../elements/Button';
import ToolTipNoIcon from '../../elements/ToolTipNoIcon';
import ToolTipOnlyIcon from '../../elements/ToolTipOnlyIcon';
import TourWrapper from '../../elements/TourWrapper';
import getCollisions from './getCollisions';
import { getArticleLink } from '../../blog';

// import { currencyCodes } from '../../misc/currencyCodes'; TODO PRD-815

// config params
import InputComponent from '../../elements/InputComponentAsyncCallback';
import AddProjectGoal from './AddProjectGoal';
import ProgressWidgetCombobox from './ProgressWidgetCombobox';
import ProjectAssetGrowthRateDialog from './ProjectAssetGrowthRate';
import getTour from '../../tours/getTour';
import { useIsProjectAccountHovered } from './projectAssetHoverStore';

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function CloseIcon() {
  return <XMarkIcon className="h-6 w-6" />;
}

function AddTransactionIcon() {
  return <PlusIcon className="-ml-0.5 mr-2 h-4 w-4 text-white" aria-hidden="true" />;
}

function AddQuoteIcon() {
  return <PlusIcon className="-ml-0.5 mr-2 h-4 w-4 text-gray-600" aria-hidden="true" />;
}

function GrowthRatesIcon() {
  return <ChartBarIcon className="-ml-0.5 mr-2 h-4 w-4 text-gray-400" aria-hidden="true" />;
}

function ApplyTemplateIcon() {
  return <ClipboardDocumentListIcon className="-ml-0.5 mr-2 h-4 w-4 text-gray-400" aria-hidden="true" />;
}

const ProjectAssetTile = React.memo(function ProjectAssetTile({ header, sum, account, baseCurrency }) {
  const isHovered = useIsProjectAccountHovered(account?.id);
  if (!account) {
    console.error('ProjectAssetTile: account is missing', header, sum);
    return null;
  }

  return (
    <div id={`project-asset-tile-${account?.id || 'none'}`} className={`p-4 ${isHovered ? 'bg-brandBlue-50' : 'bg-white'} border border-gray-300 rounded-lg shadow-sm`}>
      <div className="text-gray-500 font-bold text-sm truncate uppercase">{header}</div>
      <div>
        <span className="text-gray-900 text-xl font-bold">
          {sum.toLocaleString('de', {
            maximumFractionDigits: 0,
            style: 'currency',
            currency: baseCurrency || 'EUR',
          })}
        </span>
        {/* FIXME */}
        {/* <span className="pl-2 text-green-500 text-sm font-semibold">+48.34%</span> */}
      </div>
      <AccountLabel accountName={account.name} category={account.category} />
    </div>
  );
});
ProjectAssetTile.propTypes = {
  header: PropTypes.string.isRequired,
  sum: PropTypes.number.isRequired,
  account: PropTypes.objectOf(PropTypes.any).isRequired,
  baseCurrency: PropTypes.string,
};
ProjectAssetTile.defaultProps = {
  baseCurrency: 'EUR',
};

export default function ProjectDetails({ projectId, setProjectId, setCalendarState }) {
  // on mobile layouts, when dashboard is scrolled to the bottom, this component is rendered "above" the visible screen
  // as scrolling is disabled, the user cannot scroll to the top of the page -- so we have to do it for him
  // scroll to the top of the page (somehow it got rendered in the middle)
  const myRef = useRef(null);
  useEffect(() => {
    myRef.current.scrollIntoView({ behavior: 'instant' });
  }, []);

  const { t: tProjects, i18n } = useTranslation('projects');
  const { t: tAccountDetails } = useTranslation('accountDetails');
  const [transactionDialog, setTransactionDialog] = useState(null); // null is invisible, any value means visible (true or transaction object)
  const refTransactionDialog = useRef(undefined);
  refTransactionDialog.current = transactionDialog; // used in a listener below

  const [quoteDialog, setQuoteDialog] = useState(false);
  const refQuoteDialog = useRef(undefined);
  refQuoteDialog.current = quoteDialog; // used in a listener below

  const [assetGrowthDialog, setAssetGrowthDialog] = useState(false);
  const refAssetGrowthDialog = useRef(undefined);
  refAssetGrowthDialog.current = assetGrowthDialog; // used in a listener below

  const [goalDialog, setGoalDialog] = useState(false);
  const refGoalDialog = useRef(undefined);
  refGoalDialog.current = goalDialog; // used in a listener below

  const [templateDialog, setTemplateDialog] = useState(false);
  const [removeSubmitted, setRemoveSubmitted] = useState(false); // displays spinner when remove template button clicked
  const [collisionsDialog, setCollisionsDialog] = useState(false);

  const filterList = ['recurring', 'simulatedDividends', 'simulatedInterest', 'simulatedQuotes', 'simulatedPension'];
  // option source for the dropdown in FilterListbox
  // used at two places in ProjectTransactionList to filter transactions accordingly (in useEffect for recurring and in filterCallback for labels)
  const [filteredItems, setFilteredItems] = useState(['simulatedDividends', 'simulatedInterest', 'simulatedQuotes', 'simulatedPension']); // used to filter transactions by account; we do not want to expand all recurring txns in the beginning

  const dispatch = useDispatch();

  const project = useSelector((state) => state.projects.find((item) => item.id === projectId));
  const selectProjectTransactions = useMemo(makeSelectProjectTransactionsById, []);
  const selectProjectAssetsSummary = useMemo(makeSelectProjectAssetsSummary, []);
  const transactions = useSelector((state) => selectProjectTransactions(state, projectId)); // filter out those which belong to the current project
  const projectAssetsSummary = useSelector((state) => selectProjectAssetsSummary(state, projectId));
  const currentProjectId = projectId;

  // set to true if there is any transaction in transactions which has a tag of partOfProjectTemplate of any value
  const templateDetected = transactions?.some((txn) => txn.projectId === projectId && txn.tags?.partOfProjectTemplate);

  const alert = useSelector((state) => state.message.alert);

  // handle user input from alert modal when closing the entire form
  useEffect(() => {
    if (alert?.id === 'aboutToLoseData') {
      if (alert?.response === 'ok') {
        setProjectId(null);
      }
    }
  }, [alert?.response]);

  function handleClose(e) {
    setProjectId(null);
  }

  // if there is a dialog window open, listen for Esc key and close the dialog if it is pressed
  // otherwise close the entire form
  useEffect(() => {
    function handleEsc(e) {
      if (e.key === 'Escape') {
        if (refTransactionDialog.current) setTransactionDialog(false);
        else if (refQuoteDialog.current) setQuoteDialog(false);
        else if (refAssetGrowthDialog.current) setAssetGrowthDialog(false);
        else if (refGoalDialog.current) setGoalDialog(false);
        else setProjectId(null);
      }
    }

    document.addEventListener('keydown', handleEsc, false);
    return () => {
      document.removeEventListener('keydown', handleEsc, false);
    };
  }, []); // it needs to be one object; if it is more, it creates several listeners

  async function deleteProjectCallback() {
    if (!project?.id) return;
    await dispatch(deleteProject(project));
    setProjectId(null);
  }

  function handleDeleteProject() {
    if (!project?.id) return;
    window.dispatchEvent(
      new CustomEvent('setAlert', {
        detail: {
          id: 'youAreAboutToDeleteProject',
          caller: project.id,
          callbackOk: deleteProjectCallback,
        },
      }),
    );
  }

  const baseCurrency = useSelector((state) => state.user.profile.settings?.baseCurrency || 'EUR');
  const projectSum = projectAssetsSummary?.projectSum || 0;
  const projectAssets = projectAssetsSummary?.projectAssets || [];
  const assetsList = projectAssetsSummary?.assetsList || [];

  async function deleteTemplateTransactions() {
    if (!currentProjectId) return;
    setRemoveSubmitted(true);
    const transactionsToBeDeleted = transactions?.filter((item) => item.projectId === currentProjectId && item.tags.partOfProjectTemplate).map((item) => ({ ...item, importFlag: 'delete' }));
    await dispatch(postMixedData({ payload: transactionsToBeDeleted }));
    setRemoveSubmitted(false);
  }

  function handleTemplateButton(e) {
    if (!templateDetected) {
      setTemplateDialog(true);
    } else {
      if (!currentProjectId) return;
      window.dispatchEvent(
        new CustomEvent('setAlert', {
          detail: {
            id: 'youAreAboutToDeleteTemplateTransactions',
            caller: currentProjectId,
            callbackOk: deleteTemplateTransactions,
          },
        }),
      );
    }
  }

  function handleIsolatedFlagChange(e) {
    if (!project?.id) return;
    // if the project is currently isolated, we need to check if there are conflicts between isolated project quotes of this project and global quotes
    if (project.settings?.isIsolated) {
      // get a list of collisions (stockQuotes has the quotes for this isolated project; the function needs to run global quotes again with no parameter)
      const collisions = getCollisions(project.id);

      // if the list is not empty, display dialog (dialog to handle conflicts and runs the dispatch below)
      if (collisions.length > 0) setCollisionsDialog(collisions);
      // otherwise we can dispatch the change right away
      else {
        dispatch(
          putProject({
            ...project,
            settings: { ...project.settings, isIsolated: false },
          }),
        );
      }
    } else {
      dispatch(
        putProject({
          ...project,
          settings: { ...project.settings, isIsolated: true },
        }),
      );
    }
  }

  async function inputFieldCallback({ propertyName, newValue }) {
    const projectObject = { ...project, [propertyName]: newValue };
    let result;
    try {
      result = await dispatch(putProject(projectObject));
      dispatch(setMessage('dataUpdatedSuccessfully'));
    } catch (err) {
      dispatch(setMessage('dataUpdatedWithErrors'));
      throw new Error('accountCouldNotBeUpdated'); // the error is caught in the InputComponent
    }
    return result; // handled in InputComponent
  }

  // NOTE: '(project) &&' as well as 'project?.' is used to prevent errors after project has been deleted and the form isn't off yet (for a split second, but it happens)
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div id="projectDetailsParent" ref={myRef} className="absolute w-full h-auto min-h-full z-50 top-0 left-0 md:p-16 bg-gray-300 bg-opacity-90 space-y-6" role="dialog">
      {/* add transaction component begins here */}
      {transactionDialog && (
        <AddProjectTransaction setCalendarState={setCalendarState} transactionDialog={transactionDialog} setTransactionDialog={setTransactionDialog} projectId={projectId} stocksAssets={assetsList} />
      )}
      {quoteDialog && (
        <AddProjectQuote
          quoteDialog={quoteDialog} // this also determins if the AddTransaction component is visible;
          // ^^ values are: null, true (if user clicked New and this is empty) or a quote object (if user clicked on a quote)
          setQuoteDialog={setQuoteDialog}
          assetsList={assetsList}
          project={project}
        />
      )}
      {assetGrowthDialog && <ProjectAssetGrowthRateDialog setAssetGrowthDialog={setAssetGrowthDialog} assetsList={assetsList} project={project} />}
      {goalDialog && (
        <AddProjectGoal
          goalDialog={goalDialog} // this also determins if the AddGoal component is visible;
          // ^^ values are: null, true (if user clicked New and this is empty) or a goal object (if user clicked on a goal)
          setGoalDialog={setGoalDialog}
          project={project}
        />
      )}
      {templateDialog && (
        <ApplyProjectTemplate
          templateDialog={templateDialog} // this also determins if the AddTransaction component is visible;
          setTemplateDialog={setTemplateDialog}
          project={project}
        />
      )}
      {collisionsDialog && <CollisionsDialog collisions={collisionsDialog} setCollisionDialog={setCollisionsDialog} project={project} />}
      {/* slider */}
      <div id="projectSlider" className="lg:rounded-md lg:-mt-8 px-4 py-6 bg-white">
        <Slider theme="dark" />
      </div>
      {/* project details dialog */}
      <div
        className={`relative bg-gray-50 shadow xl:h-[85vh] sm:rounded-lg
        overflow-y-scroll scrollbar-thin scrollbar-track-rounded scrollbar-thumb-rounded scrollbar-track-gray-200 scrollbar-thumb-gray-400`}
      >
        {/* ↓ only LG and above */}
        <button type="button" id="project-details-close-button" className="hidden lg:inline-block fixed top-4 right-2 m-4 cursor-pointer">
          <XMarkIcon className="w-6 h-6 text-gray-500 hover:text-gray-600" onClick={handleClose} />
        </button>
        <a
          id="account-details-help-button"
          href={`/${i18n.language}/blog/${getArticleLink('financial-simulations-with-monestry-how-does-my-wealth-grow', i18n.language)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:inline fixed top-12 right-2 m-4 cursor-pointer"
        >
          <ToolTipNoIcon info={tAccountDetails('help')} className="-translate-x-20">
            <QuestionMarkCircleIcon className="w-6 h-6 text-brandBlue-400 hover:text-brandBlue-500" onClick={() => {}} />
          </ToolTipNoIcon>
        </a>
        {/* ↑ only LG and above */}
        <div className="relative px-4 py-5 sm:pl-8 sm:pr-10 sm:py-8 flex flex-col gap-4 sm:gap-8">
          {/* ↓ only below LG */}
          <button type="button" id="project-details-close-button" className="lg:hidden absolute top-1 right-1 m-4 cursor-pointer">
            <XMarkIcon className="w-6 h-6 text-gray-500 hover:text-gray-600" onClick={handleClose} />
          </button>
          <a
            id="account-details-help-button"
            className="lg:hidden absolute top-1 right-9 m-4 cursor-pointer"
            href={`/${i18n.language}/blog/${getArticleLink('financial-simulations-with-monestry-how-does-my-wealth-grow', i18n.language)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ToolTipNoIcon info={tAccountDetails('help')} className="-translate-x-20">
              <QuestionMarkCircleIcon className="w-6 h-6 text-brandBlue-400 hover:text-brandBlue-500" onClick={() => {}} />
            </ToolTipNoIcon>
          </a>
          {/* ↑ only below LG */}
          {/* project header */}
          <section aria-label="project header">
            <div className="flex items-center justify-between pt-4 sm:pt-0">
              <div className="flex-1 min-w-0">
                {project && (
                  <InputComponent
                    callback={inputFieldCallback}
                    parentValue={project.name}
                    formatting="sm:-ml-[0.4rem] text-2xl sm:text-3xl font-bold text-gray-900"
                    attr="name"
                    stub="Project name?"
                    showButtons
                  />
                )}
              </div>
              <div id="projectSum" className="flex-shrink-0 flex md:mt-0 md:ml-4 z-10">
                {project && (
                  <span className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                    {projectSum.toLocaleString('de', {
                      style: 'currency',
                      currency: baseCurrency || 'EUR',
                      maximumFractionDigits: 0,
                    })}
                  </span>
                )}
              </div>
            </div>
          </section>
          {/* project settings */}
          <section aria-label="project settings" className="flex gap-8 items-center">
            <div className="w-full lg:w-80 border rounded bg-white p-2 flex-shrink-0">
              {project?.settings?.progressIndicator === 'roi' && <ProgressRoi project={project} />}
              {project?.settings?.progressIndicator === 'duration' && <ProgressSlider2 projectId={project?.id} />}
              {project?.settings?.progressIndicator === 'goal' && <ProgressGoal project={project} />}
            </div>
            <div id="projectProgressIndicator" className="hidden lg:block space-y-2 text-sm text-gray-900">
              {project && <ProgressWidgetCombobox project={project} />}
            </div>
            <div id="projectIsolated" className="hidden lg:block space-y-2 text-sm text-gray-900">
              <div className="font-medium">{tProjects('visible')}</div>
              <div className="flex flex-nowrap items-center gap-2 text-xs">
                <div className="text-gray-500">{tProjects('yes')}</div>
                <Switch checked={project?.settings?.isIsolated} onChange={handleIsolatedFlagChange} className="bg-gray-300 hover:bg-gray-400 relative inline-flex h-5 w-10 items-center rounded-full">
                  <span className={`${project?.settings?.isIsolated ? 'translate-x-6' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition`} />
                </Switch>
                <div className="text-gray-500">{tProjects('noDraftMode')}</div>
                {project?.settings?.isIsolated ? (
                  <ToolTipNoIcon info={tProjects('isolatedWarning')}>
                    <ExclamationTriangleIcon className="w-5 h-5 text-brandYellow-500" />
                  </ToolTipNoIcon>
                ) : null}
              </div>
            </div>
            <div className="hidden lg:block space-y-1 text-sm text-gray-900">
              <Button
                type="button"
                id="project-details-add-goal-button"
                text={tProjects('addGoal')}
                onClick={() => {
                  setGoalDialog(true);
                }}
                size="lg"
                // formatting="inline-flex items-center border border-gray-300 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-brandBlue-600 focus:ring-offset-2 min-w-0"
              />
            </div>
            <div className="hidden lg:block space-y-1 text-sm text-gray-900">
              <Button
                type="button"
                id="project-details-delete-transaction-button"
                text={tProjects('delete')}
                onClick={handleDeleteProject}
                size="lg"
                // formatting="inline-flex items-center border border-gray-300 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-brandBlue-600 focus:ring-offset-2 min-w-0"
              />
            </div>
          </section>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 h-full max-h-full">
            <div className="xl:col-span-7 w-full h-full max-h-full">
              {/* transaction section */}
              <section aria-label="project-transactions" className="pt-2 w-full">
                {/* narrow screen version */}
                <h2 className="lg:hidden pb-4 text-xl font-bold">{tProjects('transactions')}</h2>
                {/* large screen version */}
                <div id="projectButtonBar" className="hidden lg:flex gap-4 pb-4 items-stretch">
                  <h2 className="text-xl font-bold">{tProjects('transactions')}</h2>
                  <ToolTipNoIcon info={tProjects('add')}>
                    <Button
                      type="button"
                      id="project-details-add-transaction-button"
                      text={tProjects('add')}
                      Icon={AddTransactionIcon}
                      onClick={() => {
                        setTransactionDialog(true);
                      }}
                      withAccent
                      size="lg"
                      // formatting="-mt-0.5 text-sm leading-4 focus:outline-none focus:ring-2 focus:ring-brandBlue-400 focus:ring-offset-2 min-w-0"
                    />
                  </ToolTipNoIcon>
                  <ToolTipNoIcon info={assetsList.length === 0 ? tProjects('addQuoteButtonDisabled') : tProjects('addQuoteButtonEnabled')}>
                    <Button
                      type="button"
                      enabled={assetsList.length > 0}
                      id="project-details-add-transaction-button"
                      text={tProjects('addQuote')}
                      Icon={AddQuoteIcon}
                      onClick={() => {
                        setQuoteDialog(true);
                      }}
                      size="lg"
                      textColor="text-gray-500"
                    />
                  </ToolTipNoIcon>
                  <ToolTipNoIcon info={tProjects('growthRatesDescription')}>
                    <Button
                      type="button"
                      enabled={assetsList.length > 0}
                      id="project-details-add-transaction-button"
                      text={tProjects('growthRates')}
                      Icon={GrowthRatesIcon}
                      onClick={() => {
                        setAssetGrowthDialog(true);
                      }}
                      size="lg"
                      textColor="text-gray-500"
                    />
                  </ToolTipNoIcon>
                  <ToolTipNoIcon info={tProjects('applyTemplateDescription')}>
                    <Button
                      type="button"
                      id="project-details-apply-template-button"
                      text={templateDetected ? tProjects('removeTemplate') : tProjects('applyTemplate')}
                      Icon={ApplyTemplateIcon}
                      onClick={handleTemplateButton}
                      spinnerOn={removeSubmitted}
                      size="lg"
                      textColor="text-gray-500"
                    />
                  </ToolTipNoIcon>
                  <FilterListbox filteredItems={filteredItems} setFilteredItems={setFilteredItems} filterList={filterList} />
                </div>
                {project && (
                  <ProjectTransactionList
                    project={project}
                    setTransactionDialog={setTransactionDialog}
                    setGoalDialog={setGoalDialog}
                    setQuoteDialog={setQuoteDialog}
                    projectAssets={assetsList}
                    filteredItems={filteredItems}
                  />
                )}
              </section>
              {/* notes section */}
              <h2 className="text-xl font-bold mt-8 mb-4">Notes</h2>
              {project && <Notes project={project} />}
            </div>
            {/* assets section */}
            <section aria-label="project-assets" id="project-assets" className="xl:col-span-5">
              <ToolTipNoIcon info={tProjects('assets.tooltip')} classNameOwn="mb-4">
                <h2 className="text-xl font-bold mr-1">{tProjects('assets.label')}</h2>
                <ToolTipOnlyIcon />
              </ToolTipNoIcon>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4 sticky">
                {projectAssets
                  && projectAssets.map((asset) => (
                    <ProjectAssetTile key={asset.header + asset.accountId} header={asset.header} sum={asset.sum} account={asset.account} baseCurrency={baseCurrency} />
                  ))}
              </div>
            </section>
          </div>
        </div>
      </div>
      <TourWrapper componentName="projectDetails" />
    </div>
  );
}
ProjectDetails.propTypes = {
  projectId: PropTypes.string.isRequired,
  setProjectId: PropTypes.func.isRequired,
  setCalendarState: PropTypes.func.isRequired,
};
